import { actions } from "@/actions";
import { CollectionItemSubtitleContainer } from "@/components/collection-item-subtitle/CollectionItemSubtitleContainer";
import { ShareSheetEntityKind } from "@/components/modal/share-sheet";
import { MdsDropdownItem, MdsDropdownItemKind } from "@/design-system/components/dropdown/types";
import { MdsIconKind } from "@/design-system/components/icon";
import { ItemPreviewState } from "@/design-system/components/item-list/rows/item-preview/ItemPreviewState";
import {
  MdsItemDropdown,
  MdsItemListItemRowData,
  MdsItemListRowType,
} from "@/design-system/components/item-list/types";
import { MdsItemKind } from "@/design-system/components/item-list/types/MdsItemTypes";
import { EventContext } from "@/domains/metrics/context";
import { MDSRowItemSize } from "@/mds";
import { AppStore } from "@/store/AppStore";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { CollectionsListPageStore } from "@/store/pages/CollectionsListPageStore";
import { ListStateObservable } from "@/store/pages/ListStateObservable";

export const getRowForCollectionObservable = ({
  dateLabel,
  listState,
  collectionObservable,
  store,
  trackClick,
  tabId,
  disablePreview = false,
  collectionsListPageStore,
}: {
  dateLabel?: string;
  listState?: ListStateObservable;
  collectionObservable: CollectionObservable;
  store: AppStore;
  trackClick?: () => void;
  tabId?: string;
  disablePreview?: boolean;
  collectionsListPageStore?: CollectionsListPageStore;
}) => {
  const row: MdsItemListItemRowData = {
    type: MdsItemListRowType.Item,
    key: collectionObservable.id,
    size: MDSRowItemSize.XLarge,
    payload: {
      id: collectionObservable.id,
      kind: MdsItemKind.Collection,
      titleIcon: collectionObservable.sharedAt ? { kind: MdsIconKind.Shared } : undefined,
      createPreviewState: disablePreview
        ? undefined
        : () =>
            new ItemPreviewState({
              store: collectionObservable.store,
              id: collectionObservable.id,
              kind: MdsItemKind.Collection,
            }),
      label: collectionObservable.label,
      onClick: ({ event }) => {
        store.navigation.goToCollection({
          collectionId: collectionObservable.id,
          event,
          activeTabId: tabId,
        });
        trackClick?.();
      },
      sharedBy: collectionObservable.sharedBy,
      dateLabel,
      dropdown: generateItemDropdown(collectionObservable, collectionsListPageStore),
      listState,
      extraRow: {
        id: "collection-description",
        content: () => <CollectionItemSubtitleContainer collection={collectionObservable} />,
      },
    },
  };
  return row;
};

const generateItemDropdown = (
  collectionObservable: CollectionObservable,
  collectionsListPageStore?: CollectionsListPageStore
): MdsItemDropdown | undefined => {
  if (!collectionsListPageStore) return;

  const ownerActions: MdsDropdownItem[] = collectionObservable.isOwnedByMe
    ? [
        {
          id: `delete-${collectionObservable.id}`,
          kind: MdsDropdownItemKind.Button,
          iconKind: MdsIconKind.Trash,
          label: "Delete",
          onClick: () => {
            collectionsListPageStore.deleteCollectionModal.open({
              collectionIds: [collectionObservable.id],
            });
          },
        },
      ]
    : [];
  return {
    items: [
      {
        id: `copy-link-${collectionObservable.id}`,
        kind: MdsDropdownItemKind.Button,
        label: "Copy link",
        onClick: () => {
          actions.copyCollectionLinkToClipboard({ collectionId: collectionObservable.id });
        },
        iconKind: MdsIconKind.Copy,
      },
      {
        id: `share-${collectionObservable.id}`,
        kind: MdsDropdownItemKind.Button,
        iconKind: MdsIconKind.Share,
        label: "Share",
        onClick: () =>
          collectionsListPageStore.shareSheetModal.open({
            id: collectionObservable.id,
            entityKind: ShareSheetEntityKind.Collection,
            eventContext: EventContext.CollectionsRowActions,
          }),
      },
      ...ownerActions,
    ],
  };
};
